import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// ── Types ─────────────────────────────────────────────────────
export type Client = {
  id: string;
  name: string;
  short_name: string | null;
  status: string;
  contract_ref: string | null;
  region: string | null;
  account_manager: string | null;
  accent_color: string | null;
  logo_url: string | null;
  company_id: string | null;
  is_demo: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type Vessel = {
  id: string;
  name: string;
  vessel_type: string;
  imo_number: string | null;
  status: string;
  current_position: string | null;
  specs: any;
  image_url: string | null;
  notes: string | null;
  is_demo: boolean;
  assigned_client_id: string | null;
  flag: string | null;
  class_society: string | null;
  gross_tonnage: string | null;
  contract_ref: string | null;
  created_at: string;
  updated_at: string;
};

export type CrewMember = {
  id: string;
  full_name: string;
  rank: string;
  department: string;
  nationality: string | null;
  date_of_birth: string | null;
  passport_number: string | null;
  passport_expiry: string | null;
  seafarer_id: string | null;
  email: string | null;
  phone: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  status: string;
  current_vessel_id: string | null;
  current_vessel_name: string | null;
  rotation_pattern: string | null;
  next_rotation_start: string | null;
  next_rotation_end: string | null;
  certifications: any;
  user_id: string | null;
  is_demo: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type VesselAssignment = {
  id: string;
  crew_member_id: string;
  crew_member_name: string;
  vessel_id: string;
  vessel_name: string;
  rank: string;
  rotation_start: string;
  rotation_end: string;
  status: string;
  assigned_by: string | null;
  assigned_by_name: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

// ── Vessels ───────────────────────────────────────────────────
export const useVessels = () => {
  return useQuery({
    queryKey: ["fleet_vessels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_vessels")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data || []) as unknown as Vessel[];
    },
  });
};

export const useUpsertVessel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<Vessel> & { name: string; vessel_type: string }) => {
      const { data, error } = await supabase
        .from("fleet_vessels")
        .upsert(v as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet_vessels"] });
      toast({ title: "Vessel saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
};

export const useDeleteVessel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("fleet_vessels").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet_vessels"] });
      toast({ title: "Vessel removed" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });
};

// ── Clients ───────────────────────────────────────────────────
export const useClients = () => {
  return useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase.from("clients").select("*").order("name");
      if (error) throw error;
      return (data || []) as unknown as Client[];
    },
  });
};

export const useUpsertClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: Partial<Client> & { name: string }) => {
      const { data, error } = await supabase.from("clients").upsert(c as any).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast({ title: "Client saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
};

export const useDeleteClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast({ title: "Client removed" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });
};

// ── Crew ──────────────────────────────────────────────────────
export const useCrew = () => {
  return useQuery({
    queryKey: ["crew_members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("crew_members").select("*").order("full_name");
      if (error) throw error;
      return (data || []) as unknown as CrewMember[];
    },
  });
};

export const useUpsertCrew = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: Partial<CrewMember> & { full_name: string; rank: string }) => {
      const { data, error } = await supabase.from("crew_members").upsert(c as any).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crew_members"] });
      toast({ title: "Crew member saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
};

export const useDeleteCrew = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("crew_members").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crew_members"] });
      toast({ title: "Crew member removed" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });
};

// ── Assignments ───────────────────────────────────────────────
export const useVesselAssignments = (vesselId?: string) => {
  return useQuery({
    queryKey: ["vessel_assignments", vesselId || "all"],
    queryFn: async () => {
      let q = supabase.from("vessel_assignments").select("*").order("rotation_start", { ascending: false });
      if (vesselId) q = q.eq("vessel_id", vesselId);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as unknown as VesselAssignment[];
    },
  });
};

export const useCreateAssignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Omit<VesselAssignment, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase.from("vessel_assignments").insert(a as any).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vessel_assignments"] });
      qc.invalidateQueries({ queryKey: ["crew_members"] });
      qc.invalidateQueries({ queryKey: ["crew_schedules"] });
      toast({ title: "Assignment created", description: "Crew schedule and crew status updated automatically." });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
};

// ── Danger Zone: wipe demo data ───────────────────────────────
export const useWipeDemoData = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const results = await Promise.all([
        supabase.from("vessel_assignments").delete().in("vessel_id",
          (await supabase.from("fleet_vessels").select("id").eq("is_demo", true)).data?.map(v => v.id) || []
        ),
        supabase.from("crew_members").delete().eq("is_demo", true),
        supabase.from("fleet_vessels").delete().eq("is_demo", true),
        supabase.from("clients").delete().eq("is_demo", true),
      ]);
      const failed = results.find(r => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast({ title: "Demo data wiped", description: "Ready for production data entry." });
    },
    onError: (e: any) => toast({ title: "Wipe failed", description: e.message, variant: "destructive" }),
  });
};
